/**
 * Company Routes Configuration
 *
 * Defines all routes for company user pages.
 * Sprint 1: Mock routes (React Router integration in Sprint 2+)
 */

import type { ReactElement } from 'react';
import { BarChart3, Building2, Folder, ClipboardList, Factory, Salad, Settings } from 'lucide-react';
import { CompanyDashboardPage } from '../pages/company/CompanyDashboardPage';
import { CompanyProfilePage } from '../pages/company/CompanyProfilePage';
import { DocumentVaultPage } from '../pages/company/DocumentVaultPage';
import { DocumentUploadPage } from '../pages/company/DocumentUploadPage';
import { DocumentDetailPage } from '../pages/company/DocumentDetailPage';
import { ApplicationListPage } from '../pages/company/ApplicationListPage';
import { ApplicationDetailPage } from '../pages/company/ApplicationDetailPage';
import { NewApplicationPage } from '../pages/company/NewApplicationPage';
import { SupplierListPage } from '../pages/company/SupplierListPage';
import { SupplierDetailPage } from '../pages/company/SupplierDetailPage';
import { IngredientManagementPage } from '../pages/company/IngredientManagementPage';
import { CompanySettingsPage } from '../pages/company/CompanySettingsPage';

/**
 * Route definition interface
 */
export interface RouteDefinition {
  path: string;
  element: ReactElement;
  label: string;
  icon?: ReactElement;
  showInNav?: boolean;
  children?: RouteDefinition[];
}

/**
 * Company routes configuration
 */
export const companyRoutes: RouteDefinition[] = [
  {
    path: '/company/dashboard',
    element: <CompanyDashboardPage />,
    label: 'Dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/company/profile',
    element: <CompanyProfilePage />,
    label: 'Company Profile',
    icon: <Building2 className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/company/documents',
    element: <DocumentVaultPage />,
    label: 'Document Vault',
    icon: <Folder className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/company/documents/upload',
        element: <DocumentUploadPage />,
        label: 'Upload Document',
        showInNav: false,
      },
      {
        path: '/company/documents/:id',
        element: <DocumentDetailPage />,
        label: 'Document Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/company/applications',
    element: <ApplicationListPage />,
    label: 'Applications',
    icon: <ClipboardList className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/company/applications/new',
        element: <NewApplicationPage />,
        label: 'New Application',
        showInNav: false,
      },
      {
        path: '/company/applications/:id',
        element: <ApplicationDetailPage />,
        label: 'Application Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/company/suppliers',
    element: <SupplierListPage />,
    label: 'Suppliers',
    icon: <Factory className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/company/suppliers/add',
        element: <div>Add Supplier Page (T-071)</div>,
        label: 'Add Supplier',
        showInNav: false,
      },
      {
        path: '/company/suppliers/:id',
        element: <SupplierDetailPage />,
        label: 'Supplier Details',
        showInNav: false,
      },
      {
        path: '/company/suppliers/:id/edit',
        element: <div>Edit Supplier Page (T-071)</div>,
        label: 'Edit Supplier',
        showInNav: false,
      },
    ],
  },
  {
    path: '/company/ingredients',
    element: <IngredientManagementPage />,
    label: 'Ingredients',
    icon: <Salad className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/company/settings',
    element: <CompanySettingsPage />,
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    showInNav: true,
  },
];

/**
 * Get all routes (flattened)
 */
export function getAllCompanyRoutes(): RouteDefinition[] {
  const routes: RouteDefinition[] = [];

  function flattenRoutes(routeList: RouteDefinition[]) {
    routeList.forEach((route) => {
      routes.push(route);
      if (route.children) {
        flattenRoutes(route.children);
      }
    });
  }

  flattenRoutes(companyRoutes);
  return routes;
}

/**
 * Get navigation items (routes shown in sidebar)
 */
export function getCompanyNavigationItems(): RouteDefinition[] {
  return companyRoutes.filter((route) => route.showInNav);
}

/**
 * Get route by path
 */
export function getCompanyRouteByPath(path: string): RouteDefinition | undefined {
  const allRoutes = getAllCompanyRoutes();
  return allRoutes.find((route) => route.path === path);
}

export default companyRoutes;
